import { Button } from '@shared/components/shadcn';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { LoginButton } from '../../../LoginButton';
import { useOfferTimer } from '../../hooks/useOfferTimer';

type OfferLoginButtonsProps = {
  offerValidFrom?: Date;
  buttonLoading: string | null;
};

function OfferLoginButtons({ offerValidFrom, buttonLoading }: OfferLoginButtonsProps) {
  const t = useTranslations('offers.page.obs.offer.cta');
  const { getTimerButtonText, isTimerDisabled } = useOfferTimer(offerValidFrom);

  const isButtonDisabled = (action: string) => {
    // Check if button is loading
    if (buttonLoading !== null && buttonLoading !== action.toLowerCase()) {
      return true;
    }

    // Check if offer hasn't started yet
    return isTimerDisabled();
  };

  const getButtonText = (action: string, originalText: string) => {
    if (buttonLoading === action.toLowerCase()) {
      return 'Loading...';
    }
    return getTimerButtonText(originalText);
  };

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const loginCallbackUrl = `${pathname}${search ? `?${search}` : ''}`;
  const bundleCode = searchParams.get('code') || searchParams.get('OBCODE');
  const registerHref = bundleCode ? `${ROUTES.REGISTER}?${new URLSearchParams({ code: bundleCode })}` : ROUTES.REGISTER;

  return (
    <div className="flex gap-3" data-track-container="Actions">
      <LoginButton
        variant="secondary"
        disabled={isButtonDisabled('login')}
        className="flex-1 md:flex-none w-full md:w-auto"
        data-track-name="Log In"
        callbackUrl={loginCallbackUrl}
      >
        {getButtonText('login', t('login'))}
      </LoginButton>
      <Button variant="primary" disabled={isButtonDisabled('join')} className="flex-1 md:flex-none w-full md:w-auto" data-track-name="Register" asChild>
        <Link href={registerHref} data-testid="offer-join-link">
          {getButtonText('join', t('register'))}
        </Link>
      </Button>
    </div>
  );
}

export { OfferLoginButtons };
